import React, { Component } from "react";
import { PopUp } from "./PopUp";
import { IconContext } from "react-icons";
import { FaCheck, FaPallet, FaRulerCombined, GiWeight } from "react-icons/all";
import {
  DeliveryAreaHelpOffer,
  ReadAtDatabaseService,
} from "../../services/database/ReadAtDatabaseService";
import Cards from "react-credit-cards";
import "react-credit-cards/es/styles-compiled.css";
import { RequestPayload } from "../sites/types/RequestPayload";
import { DeliveryAreas } from "../sites/constants/DeliveryAreas";
import { DeleteAtDatabaseService } from "../../services/database/DeleteAtDatabaseService";
import { UpdateAtDatabaseService } from "../../services/database/UpdateAtDatabaseService";

/**
 * React Component properties
 */
type Props = {
  // data
  isVisible: boolean;
  searchResult: RequestPayload;
  trackingID: string;

  // function
  onHide: () => void;
};

/**
 * The currently available HelpTypes
 */
export enum HelpTypes {
  DELIVERER = "deliverer",
}

/**
 * The click steps of teh pop up
 */
enum Steps {
  CHOOSE_HELP_TYPE = "chooseHelpType",
  ENTER_CREDIT_CARD = "enterCreditCard",
}

/**
 * Will be called when the user wants to book a help
 *
 * @param trackingID
 * @param selectedHelp
 * @param previouslySelected
 */
async function onPressBooked(
  trackingID: string,
  selectedHelp: HelpTypes,
  previouslySelected: HelpTypes
) {
  if (!selectedHelp && previouslySelected) {
    await DeleteAtDatabaseService.removePreviouslyBookedHelp(trackingID);
  } else {
    await UpdateAtDatabaseService.updateHelpIsNeeded(trackingID, selectedHelp);
  }
}

/**
 * Local interface to define the types of the state
 */
interface IState {
  helpType: HelpTypes | undefined;
  currentDeliveryAreaHelpOffer?: DeliveryAreaHelpOffer | null;
  currentStep: Steps;
  cvc: string;
  expiry: string;
  focus?: "name" | "number" | "expiry" | "cvc";
  name: string;
  number: string;
}

/**
 * Cross Innovation Class 2021
 *
 * Team: Kühne + Nagel
 *
 * Kunden Web App
 *
 * This component is a popUp that opens when the user needs help with the delivery
 *
 * @author Tobias Ramm <winf103614@fh-wedel,de>
 * @version last modification: 29.06.2021
 *
 */
export class NeedHelpPopUp extends Component<Props> {
  state: Readonly<IState> = {
    helpType: this.props.searchResult.bookedHelp,
    currentStep: Steps.CHOOSE_HELP_TYPE,
    cvc: "",
    expiry: "",
    name: "",
    number: "",
  };

  /**
   * componentDidMount() is invoked immediately after a component is mounted (inserted into the tree).
   * Here we load the available helpTypes for the deliveryArea
   */
  async componentDidMount(): Promise<void> {
    this.setState({
      currentDeliveryAreaHelpOffer: await ReadAtDatabaseService.getDeliveryAreaHelpOffer(
        this.props.searchResult.deliveryArea
      ),
    });
  }

  /**
   * Will be called when the user presses a field of the credit card input
   */
  handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    this.setState({ focus: e.target.name });
  };

  /**
   * Will be called when the user enters data in a credit card input
   */
  handleInputChange = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    this.setState({ [name]: value });
  };

  /**
   * Verifies if all fields are filled
   */
  checkIfValidInput(): boolean {
    const { cvc, expiry, number, name } = this.state;

    return !!cvc && !!expiry && !!number && !!name;
  }

  render() {
    const { helpType } = this.state;

    return (
      <PopUp
        isVisible={this.props.isVisible}
        onHide={() => {
          this.props.onHide();
        }}
      >
        {this.state.currentStep === Steps.CHOOSE_HELP_TYPE && (
          <div className={"need_help_pop_up_container"}>
            <h2>Sie benötigen Hilfe bei der Annahme Ihres Stückgutes?</h2>

            <div className={"delivery_information_container"}>
              {/* Maße */}
              <div className={"single_delivery_information"}>
                <IconContext.Provider
                  value={{ color: "#003369", className: "icon" }}
                >
                  <FaRulerCombined />
                </IconContext.Provider>
                <div className={"text_details"}>
                  <h4>Maße (breite x höhe x tiefe)</h4>
                  <h2>
                    {this.props.searchResult.width} m x{" "}
                    {this.props.searchResult.height} m x{" "}
                    {this.props.searchResult.depth} m
                  </h2>
                </div>
              </div>

              {/* Gewicht */}
              <div className={"single_delivery_information"}>
                <IconContext.Provider
                  value={{ color: "#003369", className: "icon" }}
                >
                  <GiWeight />
                </IconContext.Provider>
                <div className={"text_details"}>
                  <h4>Gewicht</h4>
                  <h2>{this.props.searchResult.weight}</h2>
                </div>
              </div>

              {/* Lieferart */}
              <div className={"single_delivery_information"}>
                <IconContext.Provider
                  value={{ color: "#003369", className: "icon" }}
                >
                  <FaPallet />
                </IconContext.Provider>
                <div className={"text_details"}>
                  <h4>Lieferart</h4>
                  <h2>Frei Bordsteinkannte</h2>
                </div>
              </div>
            </div>

            <h2>
              Zustellgebiet: {this.props.searchResult.deliveryArea}{" "}
              {/* @ts-ignore */}
              {DeliveryAreas[this.props.searchResult.deliveryArea]}
            </h2>

            {!!this.state.currentDeliveryAreaHelpOffer && (
              <div style={{ width: "100%" }}>
                <h3>
                  Bei Ihrem Stückgut können wir Ihnen folgende Hilfsangebote
                  machen:
                </h3>
                <p>
                  Bitte wählen Sie eine der unten aufgelisteten Hilfsangebote
                  aus.
                </p>

                <div className={"need_help_pop_up_choice_container"}>
                  <div
                    className={
                      "need_help_pop_up_choice_singleChoice " +
                      (helpType === HelpTypes.DELIVERER
                        ? " active_choice"
                        : " inactive_choice")
                    }
                    onClick={() => {
                      if (helpType === HelpTypes.DELIVERER) {
                        this.setState({
                          helpType: undefined,
                        });
                      } else {
                        this.setState({
                          helpType: HelpTypes.DELIVERER,
                        });
                      }
                    }}
                  >
                    <div
                      className={
                        "checked_icon_container" +
                        (helpType === HelpTypes.DELIVERER
                          ? " active_choice"
                          : " inactive_choice")
                      }
                    >
                      {helpType === HelpTypes.DELIVERER && (
                        <IconContext.Provider
                          value={{ color: "#6BC963", className: "icon" }}
                        >
                          <FaCheck />
                        </IconContext.Provider>
                      )}
                    </div>

                    <h3>Hilfe durch Fahrer</h3>

                    <p>
                      Der Fahrer kann bei Sperrgütern bis zu 100 kg bei der
                      Annahme helfen.
                    </p>

                    <h5>
                      Aufpreis: {this.state.currentDeliveryAreaHelpOffer.costs}
                    </h5>
                  </div>
                </div>

                <button
                  onClick={async () => {
                    this.setState({
                      currentStep: Steps.ENTER_CREDIT_CARD,
                    });
                    /* await onPressBooked(
                      this.props.trackingID,
                      helpType as HelpTypes,
                      this.props.searchResult.bookedHelp as HelpTypes
                    );
                    this.props.onHide();*/
                  }}
                  disabled={!this.state.helpType}
                  style={
                    this.state.helpType ? { opacity: 1 } : { opacity: 0.4 }
                  }
                >
                  Fortfahren
                </button>
              </div>
            )}

            {!this.state.currentDeliveryAreaHelpOffer && (
              <div style={{ width: "100%" }}>
                <h2>
                  Leider könne wir Ihnen in diesem Zustellgebiet kein
                  Hilfsangebot machen
                </h2>
              </div>
            )}
          </div>
        )}

        {this.state.currentStep === Steps.ENTER_CREDIT_CARD && (
          <div className={"need_help_pop_up_container"}>
            <h2>Buchen der Hilfe:</h2>

            <div className={"helpToBookContainer"}>
              <div className={"checked_icon_container active_choice"}>
                <IconContext.Provider
                  value={{ color: "#6BC963", className: "icon" }}
                >
                  <FaCheck />
                </IconContext.Provider>
              </div>

              <h3>Hilfe durch Fahrer</h3>

              <p>
                Der Fahrer kann bei Sperrgütern bis zu 100 kg bei der Annahme
                helfen.
              </p>

              <h1>
                {this.state.currentDeliveryAreaHelpOffer &&
                  this.state.currentDeliveryAreaHelpOffer.costs}
              </h1>
            </div>

            <h2>Bezahlen via Kreditkarte</h2>

            <div id="PaymentForm" className={"credit_card_container"}>
              <Cards
                cvc={this.state.cvc}
                expiry={this.state.expiry}
                focused={this.state.focus}
                name={this.state.name}
                number={this.state.number}
              />
              <form>
                <label>
                  Kreditkartennummer
                  <input
                    type="tel"
                    name="number"
                    placeholder="Kreditkartennummer"
                    onChange={this.handleInputChange}
                    onFocus={this.handleInputFocus}
                  />
                </label>

                <label>
                  Vorname Nachname
                  <input
                    type="text"
                    name="name"
                    placeholder="Vorname Nachname"
                    onChange={this.handleInputChange}
                    onFocus={this.handleInputFocus}
                  />
                </label>

                <div className={"row"}>
                  <label>
                    Ablaufdatum
                    <input
                      type="text"
                      name="expiry"
                      placeholder="Ablaufdatum"
                      onChange={this.handleInputChange}
                      onFocus={this.handleInputFocus}
                    />
                  </label>
                  <label>
                    CVC
                    <input
                      type="number"
                      name="cvc"
                      placeholder="CVC"
                      onChange={this.handleInputChange}
                      onFocus={this.handleInputFocus}
                    />
                  </label>
                </div>
              </form>
            </div>

            <button
              onClick={async () => {
                await onPressBooked(
                  this.props.trackingID,
                  helpType as HelpTypes,
                  this.props.searchResult.bookedHelp as HelpTypes
                );
                this.props.onHide();
              }}
              disabled={!this.checkIfValidInput()}
              style={
                this.checkIfValidInput() ? { opacity: 1 } : { opacity: 0.4 }
              }
            >
              Buchen
            </button>
          </div>
        )}
      </PopUp>
    );
  }
}
